import { masterProductsEndpoint, useDeleteMasterProductApi, useGetMasterProductUsageApi } from 'apis/master-products';
import {
  Alert,
  AlertIcon,
  Button,
  HStack,
  Link,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  UnorderedList,
  useToast,
  VStack,
} from 'design-system';
import React, { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';

interface DeleteVariantModalProps {
  isOpen: boolean;
  onClose: () => void;
  variantId: number | undefined;
  variantLabel: string;
  onDeleted: () => void;
}

export const DeleteVariantModal: React.FC<DeleteVariantModalProps> = ({
  isOpen,
  onClose,
  variantId,
  variantLabel,
  onDeleted,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  // Only fetched while the modal is open — usage is a per-variant count query and
  // there is no reason to run it for every row in the grid.
  const {
    data: usage,
    isLoading: isLoadingUsage,
    isError: isUsageError,
  } = useGetMasterProductUsageApi(variantId, {
    enabled: isOpen && !!variantId,
  });

  const { mutate: deleteVariant, isLoading: isDeleting } = useDeleteMasterProductApi({
    onSuccess: () => {
      toast({ title: `Variant "${variantLabel}" deleted`, status: 'success' });
      onDeleted();
      onClose();
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      if (status === 404) {
        // Spec: variant already gone → 404; toast and refetch. Nothing else in this
        // component gates on the error (Confirm is gated on `usage`), so without
        // this the ghost row stays in the grid, the modal stays open, and Confirm
        // stays enabled — letting the user re-fire the same 404 forever.
        toast({ title: `"${variantLabel}" was already deleted.`, status: 'error' });
        queryClient.invalidateQueries([masterProductsEndpoint]);
        onDeleted();
        onClose();
        return;
      }
      const message =
        status === 409
          ? error?.response?.data?.message || `Can’t delete "${variantLabel}" — it is still in use.`
          : error?.response?.data?.message || 'Failed to delete variant. Please try again.';
      toast({ title: message, status: 'error' });
    },
  });

  const handleConfirm = useCallback(() => {
    if (variantId !== undefined) deleteVariant(variantId);
  }, [deleteVariant, variantId]);

  const blockedBy = usage?.blockedBy ?? [];
  // Capped samples from the BE, shown as links so the user can go check the reference
  // instead of hunting for it. `hasMore*` stands in for an exact total.
  const inquiries = usage?.inquiries ?? [];
  const orders = usage?.orders ?? [];
  // react-query v3 also has an `idle` status (before the query has run at all), so
  // treating "usage is undefined and not loading" as canDelete would flash the same
  // empty-body state as a hard failure. isLoadingUsage covers idle||loading.
  const canDelete = !!usage?.canDelete && !isLoadingUsage && !isUsageError;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'full', md: 'md' }}
      scrollBehavior='inside'
      closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Variant</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align='stretch'>
            <Text>
              <strong>Variant:</strong> {variantLabel}
            </Text>

            {isLoadingUsage && (
              <HStack spacing={2}>
                <Spinner size='sm' />
                <Text color='gray.600'>Checking whether this variant can be deleted…</Text>
              </HStack>
            )}

            {!isLoadingUsage && isUsageError && (
              <Alert status='error'>
                <AlertIcon />
                Couldn&rsquo;t check whether this variant is safe to delete. Please try again.
              </Alert>
            )}

            {!isLoadingUsage && !isUsageError && usage?.isLastVariant && (
              <Alert status='error'>
                <AlertIcon />
                This is the last remaining variant of its family and can&rsquo;t be deleted.
              </Alert>
            )}

            {!isLoadingUsage && !isUsageError && !usage?.isLastVariant && blockedBy.length > 0 && (
              <Alert status='error' alignItems='flex-start'>
                <AlertIcon />
                <VStack align='stretch' spacing={1}>
                  <Text>This variant is still in use and can&rsquo;t be deleted:</Text>
                  <UnorderedList pl={4}>
                    {blockedBy.map((reason, i) => (
                      <ListItem key={`${i}-${reason}`}>{reason}</ListItem>
                    ))}
                  </UnorderedList>
                </VStack>
              </Alert>
            )}

            {!isLoadingUsage && !isUsageError && (inquiries.length > 0 || orders.length > 0) && (
              <VStack align='stretch' spacing={3}>
                {inquiries.length > 0 && (
                  <VStack align='stretch' spacing={1}>
                    <Text fontWeight='semibold' fontSize='sm'>
                      Inquiries
                    </Text>
                    <HStack spacing={2} wrap='wrap'>
                      {inquiries.map(({ inquiryId }) => (
                        <Link
                          key={inquiryId}
                          as={RouterLink}
                          to={`/app/inquiry-processor/list?inquiryId=${inquiryId}`}
                          target='_blank'
                          color='blue.600'
                          fontSize='sm'>
                          #{inquiryId}
                        </Link>
                      ))}
                      {usage?.hasMoreInquiries && (
                        <Text fontSize='sm' color='gray.600'>
                          and more
                        </Text>
                      )}
                    </HStack>
                  </VStack>
                )}

                {orders.length > 0 && (
                  <VStack align='stretch' spacing={1}>
                    <Text fontWeight='semibold' fontSize='sm'>
                      Orders
                    </Text>
                    <HStack spacing={2} wrap='wrap'>
                      {orders.map(({ orderId, code }) =>
                        // The CMS order route is keyed by code, so an order that never
                        // logged one can be listed but not linked.
                        code ? (
                          <Link
                            key={orderId}
                            as={RouterLink}
                            to={`/app/orders/details/${code}`}
                            target='_blank'
                            color='blue.600'
                            fontSize='sm'>
                            {code}
                          </Link>
                        ) : (
                          <Text key={orderId} fontSize='sm' color='gray.600'>
                            #{orderId}
                          </Text>
                        )
                      )}
                      {usage?.hasMoreOrders && (
                        <Text fontSize='sm' color='gray.600'>
                          and more
                        </Text>
                      )}
                    </HStack>
                  </VStack>
                )}
              </VStack>
            )}

            {!isLoadingUsage && !isUsageError && !usage?.isLastVariant && blockedBy.length === 0 && canDelete && (
              <Alert status='warning'>
                <AlertIcon />
                This variant isn&rsquo;t used anywhere. Deleting it removes it from the family. This action cannot be
                undone.
              </Alert>
            )}

            {!isLoadingUsage && !isUsageError && !usage?.isLastVariant && blockedBy.length === 0 && !canDelete && (
              <Alert status='warning'>
                <AlertIcon />
                Unable to determine whether this variant can be deleted. Please try again.
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant='outline' mr={3} onClick={onClose} isDisabled={isDeleting}>
            Cancel
          </Button>
          <Button
            colorScheme='red'
            onClick={handleConfirm}
            isDisabled={!canDelete}
            isLoading={isDeleting}
            loadingText='Deleting...'>
            Delete Variant
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
